import React, { useState, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  Globe,
  TrendingUp,
  MousePointer,
  Flag,
  Mail,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Settings,
  Eye,
  EyeOff,
  Settings2Icon,
  Settings2,
  BookmarkPlus,
  Plus,
  DraftingCompass,
  // Bookmark, // Commented out since it's not being used
  BarChart3,
  Loader2,
  Crown,
  Tag,
  UserSearch,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Share2,
  ExternalLink,
  Target,
  Trash2
} from 'lucide-react';
import { countryList } from './countryList';
import { CountryFlag } from './countryFlagService';
import { useTooltip, Tooltip } from './toolTip';
import EmailPopup from '../component/emailpop/EmailPopup';

const UniversalTable = ({
  data = [],
  paginationInfo = null,
  onPageChange = () => {},
  onFetchData = null, // Specific prop for data fetching
  onPageSizeChange = null, // NEW: Handler for page size changes
  onSearch = null, // NEW: Handler for server-side search
  title = "Data Table",
  searchable = true,
  defaultPageSize = 10,
  expandable = true,
  customColumns = null, // Allow custom column definitions
  renderExpandedRow = null, // Custom render function for expanded content
  loading = false,
  loadingMessage = null, // Custom loading message (e.g. "Checking relevancy...")
  userPlan,
  onUpgrade = () => {}, // Add handler for upgrade clicks
  sortByLatest = false, // Toggle state for latest affiliate sorting
  onSortByLatestChange = null, // Callback when toggle changes
  singleExpand = false, // When true, only one row can be expanded at a time
  hidePageSize = false, // When true, hide the page size selector
  renderBeforeRow = null, // Render custom content before a row (e.g. separator strip)
  onDelete = null, // Handler for deleting a prospect — receives row data
  // Affiliate-recruitment-specific: when set, the row expand renders a per-vertical
  // "Relevant Pages" section sourced from row.original.scanned_pages_json. Keys
  // in scanned_pages_json look like "GB:casino" / "GB:sports" — populated by the
  // untapped opportunity validation pipeline. When unset, the existing generic
  // "SERP Validated Pages" section renders as before (other tables unchanged).
  selectedVerticals = null,
  selectedCountries = null,
  // ISO 2-letter codes for selectedCountries (e.g. ["GB"] when selectedCountries = ["United Kingdom"]).
  // Used for scanned_pages_json key lookup since saveScannedPages stores keys by ISO code, not full name.
  // Falls back to selectedCountries if not provided.
  selectedCountryCodes = null
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // For controlled input
  // FIXED: Initialize with defaultPageSize or from paginationInfo
  const [rowsPerPage, setRowsPerPage] = useState(paginationInfo?.pageSize || defaultPageSize);
  const [emailPopupVisible, setEmailPopupVisible] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState('');
  const [selectedSocialMedia, setSelectedSocialMedia] = useState({});
  const [findingContact, setFindingContact] = useState({}); // Track loading state per row
  const [contactResults, setContactResults] = useState({}); // Store found contacts per affiliate
  const [socialMediaResults, setSocialMediaResults] = useState({}); // Store social media per affiliate
  const [pipelineStatuses, setPipelineStatuses] = useState({}); // Track CRM pipeline status per affiliate URL
  const [addingToPipeline, setAddingToPipeline] = useState({}); // Loading state for adding to pipeline
  const [userBrands, setUserBrands] = useState([]); // User's brands for pipeline brand selection
  const [brandPopup, setBrandPopup] = useState({ visible: false, affiliateUrl: '', email: '', prospectId: null, prospectBrandId: null }); // Brand selection popup state
  const [confirmingRemove, setConfirmingRemove] = useState(null); // Pipeline entry id being confirmed for removal

  // Debounce timer ref for server-side search
  const searchDebounceRef = React.useRef(null);

  // API BASE PATH
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  // DEBUG: Log onSearch prop on mount and when it changes
  useEffect(() => {
  }, [onSearch, title, searchable, loading]);

  // Handle search input change with debouncing for server-side search
  const handleSearchInputChange = (value) => {
    setSearchInputValue(value);

    if (onSearch) {
      // Server-side search with debouncing
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        onSearch(value);
      }, 500); // 500ms debounce
    } else {
      // Client-side filtering (immediate)
      setGlobalFilter(value);
    }
  };

  // Handler for contact finder with fallback
  const handleFindContact = async (affiliateUrl, rowId) => {
    setFindingContact(prev => ({ ...prev, [rowId]: true }));

    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/find-hunter-contact`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ affiliateUrl })
      });

      const result = await response.json();

      if (result.success) {
        // Store contacts
        if (result.contacts && result.contacts.length > 0) {
          const emails = result.contacts.map(c => c.value).filter(Boolean).join(', ');
          setContactResults(prev => ({ ...prev, [rowId]: emails }));
          setSelectedEmailData(emails);
          setSelectedSocialMedia(result.socialMedia || {});
          setEmailPopupVisible(true);
        }

        // Store social media
        if (result.socialMedia && Object.values(result.socialMedia).some(arr => arr && arr.length > 0)) {
          setSocialMediaResults(prev => ({ ...prev, [rowId]: result.socialMedia }));
        }

        // If no contacts or social media found - just mark as no contacts (no alert)
        if ((!result.contacts || result.contacts.length === 0) &&
            (!result.socialMedia || !Object.values(result.socialMedia).some(arr => arr && arr.length > 0))) {
          setContactResults(prev => ({ ...prev, [rowId]: 'NO_CONTACTS' }));
        }
      } else {
        setContactResults(prev => ({ ...prev, [rowId]: 'NO_CONTACTS' }));
      }
    } catch (error) {
      console.error('Error finding contacts:', error);
      setContactResults(prev => ({ ...prev, [rowId]: 'ERROR' }));
    } finally {
      setFindingContact(prev => ({ ...prev, [rowId]: false }));
    }
  };

  // Create column helper
  const columnHelper = createColumnHelper();
  const { tooltipData, tooltipPosition, showTooltip, hideTooltip, forceHideTooltip } = useTooltip();

  // Hide tooltip on scroll or click outside
  useEffect(() => {
    const handleScroll = () => {
      if (tooltipData) {
        forceHideTooltip();
      }
    };

    const handleClick = (e) => {
      // Hide tooltip when clicking anywhere
      if (tooltipData) {
        forceHideTooltip();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('click', handleClick);
    };
  }, [tooltipData, forceHideTooltip]);

  // URL formatting helper function
  const formatUrl = (url) => {
    if (!url) return '#';
    
    // If it already has a protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Add https:// if missing
    return `https://${url}`;
  };

  // Helper function to remove duplicates and normalize keywords
  const getUniqueKeywords = (keywordString) => {
    if (!keywordString) return [];
    
    const keywords = keywordString.split(',')
      .map(keyword => keyword.trim().toLowerCase()) // Normalize to lowercase for comparison
      .filter(keyword => keyword.length > 0); // Remove empty keywords
    
    // Use Set to remove duplicates, then convert back to array
    const uniqueKeywords = [...new Set(keywords)];
    
    // Return original case keywords by finding first occurrence
    return uniqueKeywords.map(uniqueKeyword => {
      const originalKeyword = keywordString.split(',')
        .map(k => k.trim())
        .find(k => k.toLowerCase() === uniqueKeyword);
      return originalKeyword;
    }).filter(Boolean);
  };

  // ADDED: Sync rowsPerPage with paginationInfo when it changes
  useEffect(() => {
    if (paginationInfo?.pageSize && paginationInfo.pageSize !== rowsPerPage) {
      setRowsPerPage(paginationInfo.pageSize);
    }
  }, [paginationInfo?.pageSize]);

  // ADDED: Fetch existing social media for all affiliates when data loads
  // This allows sharing social media found in Contact Finder across all modules
  useEffect(() => {
    const fetchExistingSocialMedia = async () => {
      if (!data || data.length === 0) return;

      // Get all affiliate URLs from the current data
      const affiliateUrls = data
        .map(row => row.affiliate_url)
        .filter(Boolean)
        .map(url => url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]);

      // Only fetch for URLs we don't already have social media for
      const urlsToFetch = affiliateUrls.filter(url => {
        // Check if any row with this URL already has social media
        const rowId = data.find(r => {
          const rowUrl = r.affiliate_url?.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          return rowUrl === url;
        })?.id;
        return rowId && !socialMediaResults[rowId];
      });

      if (urlsToFetch.length === 0) return;

      // Remove duplicates
      const uniqueUrls = [...new Set(urlsToFetch)];

      try {
        const response = await fetch(`${basePath}/api/contact-finder/social/batch`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ domains: uniqueUrls })
        });

        const result = await response.json();

        if (result.success && result.results) {
          // Map results back to row IDs
          const newSocialMedia = {};

          data.forEach(row => {
            if (!row.affiliate_url) return;
            const rowUrl = row.affiliate_url.toLowerCase()
              .replace(/^https?:\/\//, '')
              .replace(/^www\./, '')
              .split('/')[0];

            if (result.results[rowUrl]) {
              newSocialMedia[row.id] = result.results[rowUrl];
            }
          });

          if (Object.keys(newSocialMedia).length > 0) {
            setSocialMediaResults(prev => ({ ...prev, ...newSocialMedia }));
          }
        }
      } catch (error) {
        console.error('[UniversalTable] Error fetching existing social media:', error);
      }
    };

    fetchExistingSocialMedia().catch(console.error);
  }, [data, basePath]); // Re-run when data changes

  // Fetch user brands for pipeline brand selection
  useEffect(() => {
    const fetchUserBrands = async () => {
      try {
        const response = await fetch(`${basePath}/api/crm/brands`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const result = await response.json();
        if (result.success && result.data) {
          setUserBrands(result.data);
        }
      } catch (error) {
        console.error('[UniversalTable] Error fetching brands:', error);
      }
    };
    fetchUserBrands().catch(console.error);
  }, [basePath]);

  // Check CRM pipeline status for current data
  useEffect(() => {
    const checkPipelineStatuses = async () => {
      if (!data || data.length === 0) return;
      const urls = data.map(item => item.affiliate_url).filter(Boolean);
      if (urls.length === 0) return;

      try {
        const response = await fetch(`${basePath}/api/crm/check-status`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_urls: urls })
        });
        const result = await response.json();
        if (result.success) {
          setPipelineStatuses(result.data);
        }
      } catch (error) {
        console.error('[UniversalTable] Error checking pipeline statuses:', error);
      }
    };

    checkPipelineStatuses().catch(console.error);
  }, [data, basePath]);

  // Show brand selection popup when clicking Prospect
  const handleProspectClick = (e, affiliateUrl, email, prospectId, prospectBrandId) => {
    e.stopPropagation();
    e.preventDefault();
    setBrandPopup({
      visible: true,
      affiliateUrl,
      email,
      prospectId: prospectId || null,
      prospectBrandId: prospectBrandId || null
    });
  };

  // Close brand popup on outside click (with delay to avoid immediate close)
  useEffect(() => {
    if (!brandPopup.visible) return;
    const handleClickOutside = (e) => {
      const popupEl = document.getElementById('brand-pipeline-popup');
      if (popupEl && popupEl.contains(e.target)) return;
      setBrandPopup(prev => ({ ...prev, visible: false }));
    };
    // Use setTimeout to avoid the current click event triggering close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [brandPopup.visible]);

  // Remove affiliate from CRM pipeline for a specific brand
  const handleRemoveFromPipeline = async (pipelineId, affiliateUrl) => {
    try {
      const response = await fetch(`${basePath}/api/crm/${pipelineId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        // Remove entry from local state
        setPipelineStatuses(prev => {
          const existing = prev[affiliateUrl] || [];
          return {
            ...prev,
            [affiliateUrl]: existing.filter(e => e.id !== pipelineId)
          };
        });
      }
    } catch (error) {
      console.error('[UniversalTable] Error removing from pipeline:', error);
    }
    setConfirmingRemove(null);
  };

  // Add affiliate to CRM pipeline with brand_id
  const handleAddToPipeline = async (affiliateUrl, email, brandId) => {
    setBrandPopup(prev => ({ ...prev, visible: false }));
    setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: true }));
    try {
      const response = await fetch(`${basePath}/api/crm/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_url: affiliateUrl,
          email: email || null,
          source: title || 'affiliate-table',
          brand_id: brandId
        })
      });
      const result = await response.json();
      if (result.success || result.data) {
        const newEntry = {
          stage: result.data?.stage || 'prospected',
          id: result.data?.id,
          brand_id: result.data?.brand_id || brandId,
          brand_name: result.data?.brand_name
        };
        setPipelineStatuses(prev => {
          const existing = prev[affiliateUrl] || [];
          // Check if this brand already exists
          const alreadyExists = existing.some(e => e.brand_id === newEntry.brand_id);
          return {
            ...prev,
            [affiliateUrl]: alreadyExists ? existing : [...existing, newEntry]
          };
        });
      }
    } catch (error) {
      console.error('[UniversalTable] Error adding to pipeline:', error);
    } finally {
      setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: false }));
    }
  };

  const formatNumber = (num) => {
    if(num >= 1000000){
      return (num/1000000).toFixed(1) + 'M';
    }
    if(num >= 1000){
       return (num / 1000).toFixed(1) + 'K'
    }
     return num?.toLocaleString() || '0';
  };

  const getProcessedGeos = (geoData) => {
    if (!geoData) return [];

    // Countries to exclude from display
    const excludedCountries = ['unknown', 'xx', 'mobile'];

    const trimGeo = geoData.split(',');

    return trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();

        // Skip if country is empty or in excluded list
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) {
          return null;
        }

        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;

        // Also check if the short country name is in excluded list (e.g., "XX")
        if (excludedCountries.includes(shortCountryName.toLowerCase())) {
          return null;
        }

        return {
          country: shortCountryName,
          count: count?.trim() || '0'
        };
      })
      .filter(Boolean);
  };

  const formatTopGeos = (geoData) => {
    if (!geoData) return [];

    // Countries to exclude from display
    const excludedCountries = ['unknown', 'xx', 'mobile'];

    const trimGeo = geoData.split(',');

    // Filter out excluded countries and process the data
    const processedGeos = trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();

        // Skip if country is empty or in excluded list
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) {
          return null;
        }

        // Get the short country code using countryList function
        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;

        // Also check if the short country name is in excluded list (e.g., "XX")
        if (excludedCountries.includes(shortCountryName.toLowerCase())) {
          return null;
        }

        return {
          country: shortCountryName,
          count: count?.trim() || '0'
        };
      })
      .filter(Boolean); // Remove null entries
    
    // Show only first 2 countries
    const displayGeos = processedGeos.slice(0, 2);
    const remainingCount = processedGeos.length - 2;
    
    const geoElements = displayGeos.map((geo, index) => (
      <span key={index} className='mr-1'>
        <span className="text-gray-900 dark:text-slate-200 font-normal text-xs mr-1">{geo.country}</span>
        <span className="text-blue-600 dark:text-blue-400 font-semibold text-xs">[{formatNumber(geo.count)}]</span>
      </span>
    ));

    // Add comma separator and remaining count
    const result = [];
    geoElements.forEach((element, index) => {
      result.push(element);
      if (index < geoElements.length - 1) {
        result.push(<span key={`comma-${index}`} className="text-gray-400 dark:text-slate-500 text-xs">, </span>);
      }
    });

    // Add "+X more" if there are more countries
    if (remainingCount > 0) {
      result.push(<span key="comma-more" className="text-gray-400 dark:text-slate-500 text-xs">, </span>);
      result.push(
        <span key="more" className="text-gray-500 dark:text-slate-400 font-normal text-xs">
          +{remainingCount} more
        </span>
      );
    }

    return result;
  };

  // Handle email popup opening
  const handleEmailClick = (emailData) => {
    setSelectedEmailData(emailData);
    setEmailPopupVisible(true);
  };

  // Get pagination info (either from server or local) - MOVED UP BEFORE shouldShowData
  // Note: For client-side pagination, we use state-based pagination info
  // The table reference will be used after table is initialized for client-side mode
  const [clientPaginationState, setClientPaginationState] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  });

  const currentPaginationInfo = useMemo(() => {
    if (paginationInfo) return paginationInfo;

    // For client-side pagination, use the state-based pagination
    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / clientPaginationState.pageSize);

    return {
      page: clientPaginationState.pageIndex,
      pageSize: clientPaginationState.pageSize,
      total: totalRows,
      totalPages,
      hasNextPage: clientPaginationState.pageIndex < totalPages - 1,
      hasPreviousPage: clientPaginationState.pageIndex > 0
    };
  }, [paginationInfo, data.length, clientPaginationState]);

  // Function to check if row should show data or image based on plan
  const shouldShowData = (rowIndex) => {
    // Calculate the global row index across all pages
    const globalRowIndex = (currentPaginationInfo.page * currentPaginationInfo.pageSize) + rowIndex;
    
    
    if (!userPlan || userPlan === '') {
      return false; // Default to Free plan behavior if no plan
    }

    // Normalize to lowercase for case-insensitive comparison
    // (DB stores 'enterprise', UI may pass 'Enterprise' — both should work)
    const plan = userPlan.toLowerCase();

    switch (plan) {
      case 'free':
        return false; // No data rows for free plan
      case 'growth':
        const showForGrowth = globalRowIndex < 25;
        return showForGrowth; // First 25 rows show data across all pages
      case 'pro':
        const showForPro = globalRowIndex < 500;
        return showForPro; // First 500 rows show data across all pages
      case 'enterprise':
        return true; // All rows show data
      default:
        return false; // Default to Free plan behavior for unknown plans
    }
  };

  // Generate columns dynamically from data or use custom columns
  const columns = [
    columnHelper.display({
      id: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const rowIndex = row.index;
        
        if (!shouldShowData(rowIndex)) {
          return null; // Don't show action buttons for image rows
        }
        
        return (
          <div className="flex items-center justify-center space-x-2">
            <button 
              className="text-blue-600 hover:text-blue-800 transition-colors"
              onClick={() => row.toggleExpanded()}
            >
              {row.getIsExpanded() ? (
                <ChevronDown className='w-4 h-4'/>
              ) : (
                <Plus className='w-4 h-4'/>
              )}
            </button>
          </div>
        );
      },
      enableHiding: false,
      size: 100,
    }),
    
    columnHelper.accessor('affiliate_url', {
      id: 'affiliate_url',
      header: 'Affiliate URL',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        const rawUrl = getValue();
        const confidenceUrl = row.original.affiliate_url_confidence;

        // Extract root domain for Google search
        const getRootDomain = (url) => {
          if (!url) return '';
          return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
        };

        // Create Google search URL with gambling keywords (for main affiliate URL)
        const createGoogleSearchUrl = (url) => {
          const rootDomain = getRootDomain(url);
          if (!rootDomain) return null;
          const searchQuery = `site:${rootDomain} (casino OR poker OR sport OR betting)`;
          return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
        };

        // Truncate URL for display - remove protocol and show path truncated
        const truncateConfidenceUrl = (url, maxLength = 35) => {
          if (!url) return '';
          // Remove protocol
          const cleanUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
          if (cleanUrl.length <= maxLength) return cleanUrl;
          return cleanUrl.substring(0, maxLength) + '...';
        };

        // Format confidence URL for linking (ensure it has protocol)
        const formatConfidenceUrl = (url) => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          return `https://${url}`;
        };

        // Format URL for direct link to root domain
        const formatRootDomainUrl = (url) => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          return `https://${url}`;
        };

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">
            <a
              href={formatRootDomainUrl(rawUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors"
              title={rawUrl}
            >
              {rawUrl}
            </a>
          </div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('total_traffic', {
      id: 'total_traffic',
      header: 'Total Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('organic_traffic', {
      id: 'organic_traffic',
      header: 'Organic Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-gray-900 dark:text-slate-200">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('paid_traffic', {
      id: 'paid_traffic',
      header: 'Paid Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('top_geo_organic_traffic', {
      id: 'top_geo_organic_traffic',
      header: 'Top Geos',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div
            className='text-gray-900 font-medium flex justify-center items-center cursor-pointer relative'
            onMouseEnter={(e) => {
              const allGeos = getProcessedGeos(getValue());
              showTooltip(allGeos, e);
            }}
            onMouseLeave={() => {
              hideTooltip();
            }}
          >
           {formatTopGeos(getValue())}
          </div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('vertical', {
      id: 'vertical',
      header: 'Vertical',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        const vertical = getValue();
        if (!vertical) return <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>;

        // Vertical mapping - supports both legacy numeric and new text format
        const verticalMap = {
          // Legacy numeric values
          '1': 'Casino',
          '2': 'Sports Betting',
          '3': 'Lottery',
          '4': 'Poker',
          '5': 'Bingo',
          '6': 'Sweepstake',
          // New text values from DB
          'casino': 'Casino',
          'sports_betting': 'Sports Betting',
          'poker': 'Poker',
          'lottery': 'Lottery',
          'bingo': 'Bingo',
          'sweepstake': 'Sweepstake',
          'crypto': 'Crypto'
        };

        // Handle multiple verticals (comma separated) - show all verticals
        const verticals = String(vertical).split(',').map(v => v.trim()).filter(Boolean);

        return (
          <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
            {verticals.map((v, idx) => {
              const vLower = v.toLowerCase();
              const name = verticalMap[v] || verticalMap[vLower] || v;
              return (
                <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                  {name}
                </span>
              );
            })}
          </div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('email', {
      id: 'email',
      header: 'Contact',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        // Check if we found contacts for this row
        const foundContacts = contactResults[row.id];
        const socialMedia = socialMediaResults[row.id];
        const email = foundContacts && foundContacts !== 'NO_CONTACTS' && foundContacts !== 'ERROR' ? foundContacts : getValue();
        const emailCount = email ? new Set(email.split(',').map(e => e.trim()).filter(Boolean)).size : 0;
        const isLoading = findingContact[row.id];
        const affiliateUrl = row.original.affiliate_url;

        // Count social handles
        const socialCount = socialMedia ? Object.values(socialMedia).flat().filter(Boolean).length : 0;

        const getStatusText = () => {
          if (foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') {
            if (socialCount > 0) return `${socialCount} Social Found`;
            return 'No Contact Found';
          }
          if (emailCount === 0 && socialCount > 0) return `${socialCount} Social Found`;
          if (emailCount === 0) return 'No Contact';
          if (emailCount === 1) return '1 Email Found';
          return `${emailCount} Contacts Found`;
        };

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium text-center">
            {emailCount > 0 ? (
              // Has contacts - show Contact button
              <button
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
                onClick={() => handleEmailClick(email)}
              >
                <Mail className="w-3 h-3" />
                Contact
              </button>
            ) : (
              // No contacts - show Find Contact button (blue color)
              <button
                className={`${isLoading
                  ? 'bg-blue-400 cursor-wait'
                  : (foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') && socialCount === 0
                    ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 hover:shadow-lg shadow-blue-500/30'
                } text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto`}
                onClick={() => !isLoading && !(foundContacts === 'NO_CONTACTS' && socialCount === 0) && handleFindContact(affiliateUrl, row.id)}
                disabled={isLoading || ((foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') && socialCount === 0)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Finding...
                  </>
                ) : (
                  <>
                    <UserSearch className="w-3 h-3" />
                    Find Contact
                  </>
                )}
              </button>
            )}
            {/* Social media icons below button */}
            {socialMedia && socialCount > 0 && (
              <div className="flex items-center justify-center gap-1 mt-1.5">
                {socialMedia.linkedin && socialMedia.linkedin.length > 0 && (
                  <a href={socialMedia.linkedin[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Linkedin className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.twitter && socialMedia.twitter.length > 0 && (
                  <a href={socialMedia.twitter[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Twitter className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.facebook && socialMedia.facebook.length > 0 && (
                  <a href={socialMedia.facebook[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Facebook className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.instagram && socialMedia.instagram.length > 0 && (
                  <a href={socialMedia.instagram[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Instagram className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
              </div>
            )}
            <div className="text-[10px] mt-1 text-gray-500 dark:text-slate-400">
              {getStatusText()}
            </div>
          </div>
        );
      },
      enableSorting: true,
    }),

    // CRM Pipeline column - separate column for adding affiliates to pipeline
    columnHelper.display({
      id: 'pipeline',
      header: () => (
        <div className="flex items-center justify-center gap-1">
          <Target className="w-4 h-4 text-green-600" />
          <span className="text-xs font-medium text-green-600 dark:text-green-400 tracking-wider">Pipeline</span>
        </div>
      ),
      cell: ({ row }) => {
        const rowIndex = row.index;
        if (!shouldShowData(rowIndex)) return null;

        const affiliateUrl = row.original.affiliate_url;
        const email = row.original.email;
        const pipelineEntries = pipelineStatuses[affiliateUrl]; // Now an array

        const stageColors = {
          prospected: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          contacted: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
          negotiating: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
          onboarding: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
          active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          churned: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        };

        return (
          <div className="flex flex-col items-center gap-1">
            {/* Show existing brand pipeline statuses */}
            {pipelineEntries && pipelineEntries.length > 0 && (
              <div className="flex flex-col gap-1">
                {pipelineEntries.map((entry, idx) => {
                  const colorClass = stageColors[entry.stage] || 'bg-gray-100 text-gray-700';
                  return (
                    <span key={idx} className={`inline-flex items-center text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${colorClass}`} title={`${entry.brand_name}: ${entry.stage}`}>
                      {entry.brand_name}: {entry.stage}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Always show Prospect button to add for more brands */}
            <button
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transform hover:scale-105 hover:shadow-lg shadow-green-500/30 text-white text-[10px] font-medium py-0.5 px-2 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
              onClick={(e) => handleProspectClick(e, affiliateUrl, email, row.original.id, row.original.brand_id)}
              disabled={addingToPipeline[affiliateUrl]}
            >
              {addingToPipeline[affiliateUrl] ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  Prospect
                </>
              )}
            </button>
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  // Configure table
  const table = useReactTable({
    data,
    columns,
    // Key rows by the affiliate's real id so per-row state (contactResults,
    // socialMediaResults, findingContact) stays bound to the affiliate across
    // pagination/sort/search. Without this, tanstack defaults row.id to the
    // array index, and state leaks between different affiliates that happen
    // to land on the same index after the data changes.
    getRowId: (row, index) => (row?.id != null ? String(row.id) : String(index)),
    state: {
      sorting,
      globalFilter,
      expanded,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: (updater) => {
      if (singleExpand) {
        // When singleExpand is true, collapse all other rows and expand only the clicked one
        setExpanded((old) => {
          const newExpanded = typeof updater === 'function' ? updater(old) : updater;
          // Find which row is being expanded (not collapsed)
          const expandedKeys = Object.keys(newExpanded).filter(key => newExpanded[key]);
          const oldExpandedKeys = Object.keys(old).filter(key => old[key]);

          if (expandedKeys.length > 1) {
            // Multiple rows in expanded state - find the NEW one (the one that was just clicked)
            // The new key is the one that wasn't in the old state
            const newKey = expandedKeys.find(key => !oldExpandedKeys.includes(key));
            if (newKey) {
              return { [newKey]: true };
            }
            // Fallback: keep the first one
            return { [expandedKeys[0]]: true };
          } else if (expandedKeys.length === 1) {
            // Only one row expanded, keep it (could be toggling same row)
            return { [expandedKeys[0]]: true };
          }
          // No rows expanded (user collapsed the only expanded row)
          return newExpanded;
        });
      } else {
        setExpanded(updater);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(paginationInfo ? {
      // Server-side pagination
      manualPagination: true,
      manualFiltering: !!onSearch, // Disable client-side filtering when server-side search is enabled
      pageCount: paginationInfo.totalPages,
    } : {
      // Client-side pagination
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize: defaultPageSize,
        },
      },
    }),
    globalFilterFn: 'includesString',
    getRowCanExpand: () => expandable,
  });

  // Handle pagination
  const handlePageChange = (newPage) => {
    
    // Validate the new page number
    if (newPage < 0 || newPage >= currentPaginationInfo.totalPages) {
      return;
    }
    
    if (paginationInfo && onFetchData) {
      // Server-side pagination - use onFetchData (preferred)
      onFetchData(newPage);
    } else if (paginationInfo && onPageChange) {
      // Fallback to onPageChange for server-side
      onPageChange(newPage);
    } else {
      // Client-side pagination
      table.setPageIndex(newPage);
      // Also update client pagination state
      setClientPaginationState(prev => ({ ...prev, pageIndex: newPage }));
    }
  };

  // FIXED: Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setRowsPerPage(newPageSize);

    if (paginationInfo && onPageSizeChange) {
      // Server-side pagination - notify parent component
      onPageSizeChange(newPageSize);
    } else if (paginationInfo && onFetchData) {
      // Alternative: Call onFetchData with current page and new page size
      // Note: You might need to modify the parent component to handle page size in onFetchData
      // For now, we'll assume onPageSizeChange is the preferred method
    } else {
      // Client-side pagination
      table.setPageSize(newPageSize);
      // Also update client pagination state and reset to first page
      setClientPaginationState({ pageIndex: 0, pageSize: newPageSize });
    }
  };

  // Generate pagination numbers with proper ellipsis
  const generatePaginationNumbers = () => {
    const currentPage = currentPaginationInfo.page + 1; // Convert to 1-based
    const totalPages = currentPaginationInfo.totalPages;
    const pages = [];

    if (totalPages <= 1) return pages;

    // Only show previous, current, and next page — no jumping allowed
    if (currentPage > 1) pages.push(currentPage - 1);
    pages.push(currentPage);
    if (currentPage < totalPages) pages.push(currentPage + 1);

    return pages;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6 transition-colors duration-300">
        <div className="text-center py-8">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
              <div className="text-gray-600 dark:text-slate-300 text-lg font-medium">
                {loadingMessage || 'Loading data...'}
              </div>
            </div>
          ) : (
            <>
              <div className="text-gray-500 dark:text-slate-400 text-lg">No data available</div>
              <div className="text-gray-400 dark:text-slate-500 text-sm mt-2">Try adjusting your filters or search criteria</div>
            </>
          )}
        </div>
      </div>
    );
  }

  const renderHeaderContent = (header) => {
    const getHeaderIcon = (headerId) => {
      switch (headerId) {
        case 'action':
          return null;
        case 'affiliate_url':
          return <Globe className='w-4 h-4'/>;
        case 'total_traffic':
          return <BarChart3 className='w-4 h-4'/>;
        case 'organic_traffic':
          return <TrendingUp className='w-4 h-4'/>;
        case 'paid_traffic':
          return <MousePointer className='w-4 h-4'/>;
        case 'top_geo_organic_traffic':
          return <Flag className='w-4 h-4'/>;
        case 'email':
          return <Mail className='w-4 h-4'/>;
        case 'vertical':
          return <Tag className='w-4 h-4'/>;
        default:
          return <BarChart3 className='w-4 h-4'/>;
      }
    };

    return (
      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 dark:text-blue-400 tracking-wider hover:text-blue-700 dark:hover:text-blue-300 transition-colors w-full">
        {getHeaderIcon(header.id)}
        {flexRender(header.column.columnDef.header, header.getContext())}
      </button>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm transition-colors duration-300">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            {currentPaginationInfo.total ? `${currentPaginationInfo.total} affiliates found` : `${data.length} affiliates found`}
          </p>
        </div>

        <div className='flex items-center space-x-4'>
          {searchable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by URL, keywords..."
                value={onSearch ? searchInputValue : (globalFilter ?? '')}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 text-sm transition-all duration-200 hover:shadow-md focus:shadow-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
              />
            </div>
          )}

          {/* Newly Added Affiliate Toggle */}
          {onSortByLatestChange && (
            <div className="flex items-center h-[38px] px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mr-2 whitespace-nowrap">Newly Added Affiliate</label>
              <button
                type="button"
                onClick={() => {
                  onSortByLatestChange(!sortByLatest);
                }}
                disabled={loading}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                  sortByLatest ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    sortByLatest ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* FIXED: Page size selector - hidden when hidePageSize is true */}
          {!hidePageSize && (
            <div className="relative">
              <select
                value={rowsPerPage}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                disabled={loading}
                className="border border-gray-200 dark:border-slate-600 rounded-md px-3 py-2 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 transition-all duration-200 hover:shadow-md focus:shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          )}
          
          {/* Shortlist and Settings buttons - COMMENTED OUT */}
          {/* <div className="relative flex items-center space-x-3">
            <button className='relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md'>
              <BookmarkPlus className='text-gray-400 h-4 w-4'/>
              <span className='text-sm font-medium text-gray-700'>Shortlist</span>
              <div className='absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium'>
                1
              </div>
            </button>
            <button className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md'>
              <Settings2 className='text-gray-400 h-4 w-4'/>
              <span className='text-sm font-medium text-gray-700'>Settings</span>
            </button>
          </div> */}
        </div>
      </div>

      {/* Table Container with Loading Overlay */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-xl">
            <div className="flex flex-col items-center gap-3 px-6 py-4 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
              <div className="text-center">
                <div className="text-gray-800 dark:text-slate-200 font-semibold text-sm">
                  {loadingMessage || 'Loading...'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} className="px-6 py-4 text-center whitespace-nowrap">
                      {header.isPlaceholder ? null : (
                        renderHeaderContent(header)
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
              {table.getRowModel().rows.map((row, rowIdx) => (
                <React.Fragment key={row.id}>
                  {renderBeforeRow && renderBeforeRow(row.original, rowIdx, table.getRowModel().rows)}
                  <tr className={`${shouldShowData(row.index) ? 'hover:bg-gray-50 dark:hover:bg-slate-800' : 'group hover:bg-gray-50 dark:hover:bg-slate-800'} transition-all duration-200 relative`}>
                    {shouldShowData(row.index) ? (
                      // Show normal data cells
                      row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-slate-200"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))
                    ) : (
                      // Show blurred image row with upgrade overlay
                      <td colSpan={row.getVisibleCells().length} className="p-0 relative">
                        {/* Blurred image */}
                        <img 
                          src="./blurrow.png" 
                          alt={`Preview of ${row.original.affiliate_url || 'affiliate'}`}
                          className="w-full object-cover"
                          onError={(e) => { 
                            // Fallback to blurred content if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        
                        {/* Fallback blurred content (hidden by default) */}
                        <div className="blur-sm opacity-60 pointer-events-none" style={{ display: 'none' }}>
                          <div className="flex items-center justify-between px-6 py-4">
                            <div className="flex items-center gap-4">
                              <div className="w-6 h-6 bg-gray-200 rounded"></div>
                              <div className="space-y-1">
                                <div className="h-4 bg-gray-200 rounded w-32"></div>
                                <div className="h-3 bg-gray-200 rounded w-24"></div>
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-20"></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Hover Overlay with Upgrade Button */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-75 transition-all duration-300 flex items-center justify-center">
                          <button
                            onClick={() => {
                              // Navigate to pricing plan page
                              window.location.href = '/pricing-plan';
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium px-6 py-3 rounded-lg shadow-lg hover:shadow-xl flex items-center gap-2"
                          >
                            <Crown className="w-5 h-5" />
                            <span>Upgrade to View Data</span>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expanded Row Content - only for accessible data rows */}
                  {row.getIsExpanded() && shouldShowData(row.index) && (
                    <tr>
                      <td colSpan={row.getVisibleCells().length} className="px-6 py-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800 border-t dark:border-slate-700">
                        <div className="space-y-6">
                          {/* Custom expanded content or default content */}
                          {renderExpandedRow ? (
                            renderExpandedRow(row.original)
                          ) : (
                            <div className="space-y-6">
                              {/* Per-Vertical Relevant Pages — affiliate recruitment only.
                                  Reads scanned_pages_json populated by the untapped opportunity
                                  validation pipeline. Keys are "{country}:{vertical}".
                                  Renders one section per selected vertical (e.g. Casino + Sports
                                  if both selected). Falls through to the legacy SERP Validated
                                  Pages block below when no per-vertical data exists. */}
                              {(() => {
                                if (!selectedVerticals || selectedVerticals.length === 0) return null;
                                if (!selectedCountries || selectedCountries.length === 0) return null;
                                const scanned = row.original.scanned_pages_json || {};
                                if (!scanned || typeof scanned !== 'object' || Object.keys(scanned).length === 0) return null;

                                // Map UI vertical name → DB vertical key (matches fetchData.js VERTICAL_NAME_MAPPING)
                                const verticalKeyMap = {
                                  'casino': 'casino',
                                  'sports betting': 'sports_betting',
                                  'sports': 'sports_betting',
                                  'poker': 'poker',
                                  'lottery': 'lottery',
                                  'bingo': 'bingo',
                                  'sweepstake': 'sweepstake',
                                  'sweepstakes': 'sweepstake',
                                  'crypto': 'crypto'
                                };

                                // Country ISO code resolution.
                                // selectedCountryCodes (if provided) contains ISO 2-letter codes matching
                                // the keys written by saveScannedPages (e.g. "GB"). If not provided, fall
                                // back to selectedCountries which may be full names like "United Kingdom".
                                // Either way, expand GB/UK aliases so both storage variants are tried.
                                const expandCountryAliases = (code) => {
                                  const upper = (code || '').toUpperCase().trim();
                                  if (upper === 'GB' || upper === 'UK') return ['GB', 'UK'];
                                  return [upper];
                                };
                                // Use ISO codes when available, otherwise use raw selectedCountries values
                                const lookupCountries = (selectedCountryCodes && selectedCountryCodes.length > 0)
                                  ? selectedCountryCodes
                                  : selectedCountries;

                                // For each (country × vertical), look up RELEVANT scanned pages only.
                                // saveScannedPages normalises the vertical key by stripping _ and spaces
                                // (sports_betting → sportsbetting) so we try both forms.
                                // Only pages with isRelevant===true are included.
                                const sections = [];
                                for (const vert of selectedVerticals) {
                                  const dbVert = verticalKeyMap[vert.trim().toLowerCase()] || vert.trim().toLowerCase();
                                  const normVert = dbVert.replace(/[_\s]+/g, ''); // matches saveScannedPages key format
                                  const collectedPages = [];
                                  for (const country of lookupCountries) {
                                    const countryCodes = expandCountryAliases(country);
                                    for (const cc of countryCodes) {
                                      // Try normalised key first (e.g. "GB:casino", "GB:sportsbetting")
                                      // then original dbVert for backward compat ("GB:sports_betting")
                                      const entries =
                                        scanned[`${cc}:${normVert}`] ||
                                        scanned[`${cc}:${dbVert}`] ||
                                        // Legacy format: bare country key (old casino-only data)
                                        (dbVert === 'casino' ? scanned[cc] : null) ||
                                        [];
                                      if (Array.isArray(entries)) {
                                        for (const p of entries) {
                                          if (!p) continue;
                                          const url = typeof p === 'string' ? p : p.url;
                                          if (!url) continue;
                                          // Only show pages the pipeline confirmed as relevant
                                          const isRelevant = typeof p === 'string' ? true : (p.isRelevant === true);
                                          if (isRelevant) {
                                            collectedPages.push({ url, country: cc, confidence: p.confidence || 0 });
                                          }
                                        }
                                      }
                                    }
                                  }

                                  // Dedupe URLs
                                  const seen = new Set();
                                  const uniquePages = collectedPages.filter(p => {
                                    const k = p.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                    if (seen.has(k)) return false;
                                    seen.add(k);
                                    return true;
                                  });

                                  if (uniquePages.length > 0) {
                                    sections.push({ vertical: vert, dbVertical: dbVert, pages: uniquePages });
                                  }
                                }

                                if (sections.length === 0) return null;

                                // Color per vertical
                                const verticalColors = {
                                  'casino': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
                                  'sports_betting': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                                  'poker': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                                  'lottery': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                                  'bingo': 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
                                  'sweepstake': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
                                  'crypto': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                };

                                // Flatten all sections into a single deduplicated page list
                                // (one section per vertical, but we render as a single list like Top Pages)
                                const allPages = [];
                                const globalSeen = new Set();
                                for (const section of sections) {
                                  for (const page of section.pages) {
                                    const k = page.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                    if (!globalSeen.has(k)) {
                                      globalSeen.add(k);
                                      allPages.push({ ...page, vertical: section.vertical });
                                    }
                                  }
                                }

                                // Country display name for the "Relevant for X" tag
                                const countryDisplayName = selectedCountries && selectedCountries[0]
                                  ? selectedCountries[0].replace(/^United Kingdom.*/, 'UK').replace(/^United States.*/, 'US')
                                  : (selectedCountryCodes && selectedCountryCodes[0]) || '';

                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Relevant Pages</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {allPages.map((page, idx) => (
                                          <div key={idx} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded flex-shrink-0">{idx + 1}</span>
                                              <a
                                                href={page.url.startsWith('http') ? page.url : `https://${page.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block transition-colors"
                                              >
                                                {page.url}
                                              </a>
                                            </div>
                                            {countryDisplayName && (
                                              <div className="pl-8">
                                                <span className="text-[10px] text-green-700 dark:text-green-400 font-medium">
                                                  Relevant for {countryDisplayName}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* SERP Validated Pages - Show multiple URLs on separate lines */}
                              {/* Shown ONLY when the Relevant Pages block above rendered nothing
                                  (i.e. scanned_pages_json has no isRelevant===true entry for the
                                  selected country+vertical). Falls through to this block for affiliates
                                  that were validated via a fast path (TLD classification, blocked news,
                                  etc.) where no scanned pages were saved. When no filters are active,
                                  this block always shows as before. */}
                              {(() => {
                                // Suppress SERP block when the Relevant Pages block above already
                                // rendered isRelevant===true pages (avoid showing both).
                                // Falls through when: no filters set, scanned_pages_json is empty,
                                // or no relevant pages found (affiliate has no casino pages).
                                if (selectedVerticals && selectedVerticals.length > 0 &&
                                    selectedCountries && selectedCountries.length > 0) {
                                  const scanned = row.original.scanned_pages_json || {};
                                  if (scanned && typeof scanned === 'object' && Object.keys(scanned).length > 0) {
                                    const vkMap = {
                                      'casino': 'casino', 'sports betting': 'sports_betting', 'sports': 'sports_betting',
                                      'poker': 'poker', 'lottery': 'lottery', 'bingo': 'bingo',
                                      'sweepstake': 'sweepstake', 'sweepstakes': 'sweepstake', 'crypto': 'crypto'
                                    };
                                    const expA = (c) => { const u = (c || '').toUpperCase().trim(); return (u === 'GB' || u === 'UK') ? ['GB', 'UK'] : [u]; };
                                    const lkCodes = (selectedCountryCodes && selectedCountryCodes.length > 0) ? selectedCountryCodes : selectedCountries;
                                    for (const vert of selectedVerticals) {
                                      const dv = vkMap[vert.trim().toLowerCase()] || vert.trim().toLowerCase();
                                      const nv = dv.replace(/[_\s]+/g, '');
                                      for (const country of lkCodes) {
                                        for (const cc of expA(country)) {
                                          const entries = scanned[`${cc}:${nv}`] || scanned[`${cc}:${dv}`] || (dv === 'casino' ? scanned[cc] : null) || [];
                                          if (Array.isArray(entries) && entries.some(p => p && (typeof p === 'string' || p.isRelevant === true))) {
                                            return null; // Relevant Pages block rendered → hide SERP block
                                          }
                                        }
                                      }
                                    }
                                  }
                                }

                                if (!row.original.serp_validated_page && !row.original.affiliate_url_confidence) return null;

                                // Parse and deduplicate URLs from both fields
                                const splitUrls = (str) => {
                                  if (!str) return [];
                                  const matches = str.match(/https?:\/\/[^\s,]+/g);
                                  if (!matches) return [];
                                  return matches.map(u => u.replace(/[,;]+$/, '').trim()).filter(u => u.length > 10);
                                };
                                let urls = splitUrls(row.original.affiliate_url_confidence);
                                for (const u of splitUrls(row.original.serp_validated_page)) {
                                  const norm = u.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                  if (!urls.some(x => x.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '') === norm)) {
                                    urls.unshift(u);
                                  }
                                }
                                const seen = new Set();
                                const uniqueUrls = urls.filter(u => {
                                  const k = u.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                  if (seen.has(k)) return false;
                                  seen.add(k);
                                  return true;
                                });

                                if (uniqueUrls.length === 0) return null;

                                const countryLabel = selectedCountries && selectedCountries[0]
                                  ? selectedCountries[0].replace(/^United Kingdom.*/, 'UK').replace(/^United States.*/, 'US')
                                  : '';

                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Relevant Pages</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {uniqueUrls.map((url, idx) => (
                                          <div key={idx} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded flex-shrink-0">{idx + 1}</span>
                                              <a
                                                href={url.startsWith('http') ? url : `https://${url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block transition-colors"
                                              >
                                                {url}
                                              </a>
                                            </div>
                                            {countryLabel && (
                                              <div className="pl-8">
                                                <span className="text-[10px] text-green-700 dark:text-green-400 font-medium">
                                                  Relevant for {countryLabel}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Pipeline / Prospected Brands */}
                              {(() => {
                                const entries = pipelineStatuses[row.original.affiliate_url] || [];
                                if (entries.length === 0) return null;
                                const stageColorMap = {
                                  prospected: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
                                  contacted: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
                                  negotiating: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
                                  onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800',
                                  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
                                  churned: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                                };
                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Target className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Prospected Brands</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
                                      <div className="flex flex-wrap gap-2">
                                        {entries.map((entry, idx) => (
                                          <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${stageColorMap[entry.stage] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                            <span className="text-sm font-semibold">{entry.brand_name}</span>
                                            <span className="text-[10px] uppercase font-bold opacity-75">{entry.stage}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Live Affiliate Brand Page - Where the brand is listed */}
                              {row.original.live_affiliate_brand_url && Object.keys(row.original.live_affiliate_brand_url).length > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <ExternalLink className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Brand Listed On</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
                                    <div className="space-y-2">
                                      {Object.entries(row.original.live_affiliate_brand_url).map(([brand, pageUrl], idx) => (
                                        <div key={idx} className="flex items-center gap-3 py-1">
                                          <span className="text-xs font-medium text-gray-600 dark:text-slate-400 min-w-[120px]">{brand}</span>
                                          <a
                                            href={pageUrl.startsWith('http') ? pageUrl : `https://${pageUrl}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors flex items-center gap-2 text-sm"
                                          >
                                            <span className="break-all">{pageUrl}</span>
                                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-3 pt-2 border-t dark:border-slate-700">
                                      Verified pages where the brand is actively listed or promoted.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Country Breakdown - Full Width */}
                       <div className="space-y-3">
  <div className="flex items-center gap-2">
    <Flag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Country Breakdown</h4>
  </div>
  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
    {row.original.top_geo_organic_traffic ? (
      <div className="grid grid-cols-4 gap-4">
        {getProcessedGeos(row.original.top_geo_organic_traffic).map((geo, index) => (
          <div key={index} className="flex justify-between items-center py-3 px-4 bg-gray-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
            <div className="flex items-center gap-3">
              <CountryFlag countryCode={geo.country} className="w-6 h-4" />
              <span className="font-medium text-gray-800 dark:text-slate-200">{geo.country}</span>
            </div>
            <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatNumber(geo.count)}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No country data available</div>
    )}
  </div>
</div>
                              {/* Two Column Layout for Pages and Keywords */}
                              <div className="grid grid-cols-2 gap-6">
                                {/* Top Pages */}
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Pages</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                    {row.original.top_page ? (
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {(() => {
                                          // Split, trim, filter empty values, and remove duplicates
                                          const pages = [...new Set(
                                            row.original.top_page
                                              .split(',')
                                              .map(p => p.trim())
                                              .filter(p => p && p.length > 0)
                                          )].slice(0, 10);

                                          return pages.map((page, index) => (
                                            <div key={index} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">{index + 1}</span>
                                                <a
                                                  href={formatUrl(page)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block pl-6 transition-colors"
                                                >
                                                  {page}
                                                </a>
                                              </div>
                                            </div>
                                          ));
                                        })()}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No top pages available</div>
                                    )}
                                  </div>
                                </div>

                                {/* Top Organic Keywords */}
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Organic Keywords</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                    {row.original.keywords ? (
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {getUniqueKeywords(row.original.keywords).slice(0, 10).map((keyword, index) => (
                                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-2 py-1 rounded">{index + 1}</span>
                                              <span className="text-gray-700 dark:text-slate-300 text-xs">{keyword}</span>
                                            </div>
                                            <span className="text-green-600 dark:text-green-400 text-xs font-medium">Organic</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No organic keywords available</div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Paid Keywords - Full Width */}
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <MousePointer className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Paid Keywords</h4>
                                </div>
                                <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                  {row.original.paid_keyword ? (
                                    <div className="grid grid-cols-3 gap-3">
                                      {getUniqueKeywords(row.original.paid_keyword).slice(0, 12).map((keyword, index) => (
                                        <div key={index} className="flex justify-between items-center py-2 px-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-800">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 px-2 py-1 rounded">{index + 1}</span>
                                            <span className="text-gray-700 dark:text-slate-300 text-xs">{keyword}</span>
                                          </div>
                                          <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">Paid</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-gray-500 dark:text-slate-400 text-center py-4">No paid keywords available</div>
                                  )}
                                </div>
                              </div>

                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FIXED: Move Tooltip outside of pagination conditional - Always render tooltip */}
      <Tooltip 
        data={tooltipData} 
        position={tooltipPosition} 
        formatNumber={formatNumber} 
      />

      {/* Pagination */}
      {currentPaginationInfo.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-slate-300">
              Showing {currentPaginationInfo.page * currentPaginationInfo.pageSize + 1} to{' '}
              {Math.min((currentPaginationInfo.page + 1) * currentPaginationInfo.pageSize, currentPaginationInfo.total)} of{' '}
              {currentPaginationInfo.total} results
            </div>

            <div className="flex items-center gap-1">
              {/* Previous button */}
              <button
                onClick={() => handlePageChange(currentPaginationInfo.page - 1)}
                disabled={!currentPaginationInfo.hasPreviousPage || loading}
                className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page numbers */}
              {generatePaginationNumbers().map((pageNum, index) => {
                if (pageNum === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500 dark:text-slate-400">
                      ...
                    </span>
                  );
                }

                const isCurrentPage = pageNum === currentPaginationInfo.page + 1;

                return (
                  <button
                    key={pageNum}
                    onClick={() => {
                      const targetPage = pageNum - 1; // Convert to 0-based
                      handlePageChange(targetPage);
                    }}
                    disabled={loading}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      isCurrentPage
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-100'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                onClick={() => handlePageChange(currentPaginationInfo.page + 1)}
                disabled={!currentPaginationInfo.hasNextPage || loading}
                className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Selection Popup for Pipeline - Centered Modal */}
      {brandPopup.visible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => { setBrandPopup(prev => ({ ...prev, visible: false })); setConfirmingRemove(null); }}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            id="brand-pipeline-popup"
            className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-600 p-5 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Select Brand for Prospecting</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate max-w-[250px]">{brandPopup.affiliateUrl}</p>
              </div>
              <button onClick={() => { setBrandPopup(prev => ({ ...prev, visible: false })); setConfirmingRemove(null); }} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {userBrands.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No brands found. Add a brand first.</p>
              ) : (
                userBrands.map(brand => {
                  const existingEntries = pipelineStatuses[brandPopup.affiliateUrl] || [];
                  const pipelineEntry = existingEntries.find(e => e.brand_id === brand.id);
                  const alreadyAdded = !!pipelineEntry;
                  const isConfirming = confirmingRemove === pipelineEntry?.id;

                  // Show inline confirmation for this brand
                  if (alreadyAdded && isConfirming) {
                    return (
                      <div key={brand.id} className="px-4 py-3 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                        <p className="text-red-700 dark:text-red-300 text-xs mb-2">Remove <strong>{brand.brand_name || brand.brand_url}</strong>?</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRemoveFromPipeline(pipelineEntry.id, brandPopup.affiliateUrl)}
                            className="flex-1 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                          >
                            Yes, Remove
                          </button>
                          <button
                            onClick={() => setConfirmingRemove(null)}
                            className="flex-1 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={brand.id}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all flex items-center justify-between border ${
                        alreadyAdded
                          ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-slate-500 border-gray-100 dark:border-slate-700'
                          : 'hover:bg-green-50 dark:hover:bg-green-900/20 text-gray-700 dark:text-gray-200 border-gray-100 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-800 hover:shadow-sm cursor-pointer'
                      }`}
                      onClick={() => !alreadyAdded && handleAddToPipeline(brandPopup.affiliateUrl, brandPopup.email, brand.id)}
                    >
                      <span className="truncate font-medium">{brand.brand_name || brand.brand_url}</span>
                      {alreadyAdded ? (
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-[10px] bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-400 px-2 py-0.5 rounded-full whitespace-nowrap">Already added</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmingRemove(pipelineEntry.id); }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Remove from pipeline"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <Plus className="w-4 h-4 text-green-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Popup */}
      <EmailPopup
        isOpen={emailPopupVisible}
        onClose={() => { setEmailPopupVisible(false); setSelectedSocialMedia({}); }}
        recipient={{ email: selectedEmailData?.split(',')[0]?.trim() || '', value: selectedEmailData?.split(',')[0]?.trim() || '' }}
        allEmails={selectedEmailData ? selectedEmailData.split(',').map(e => e.trim()).filter(Boolean) : []}
        socialMedia={selectedSocialMedia}
        onSend={async (emailForm, recipient) => {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
          const response = await fetch(`${apiUrl}/api/emailsent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ to: emailForm.to, cc: emailForm.cc || '', subject: emailForm.subject, message: emailForm.message, attachments: emailForm.attachments || [], sender_email_id: emailForm.sender_email_id || 'primary' })
          });
          if (!response.ok) {
            const errText = await response.text().catch(() => '');
            throw new Error(errText || `Failed to send email (${response.status})`);
          }
          const result = await response.json().catch(() => ({}));
          if (!result.success) throw new Error(result.error || 'Failed to send email');
        }}
      />
    </div>
  );
};

export default UniversalTable;